import { router, usePage } from "@inertiajs/react"
import { Building2, Check, ChevronsUpDown, Plus, SlidersHorizontal, Store } from "lucide-react"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"
import { t } from "@/lib/i18n"

type NavLocation = {
  id: number
  name: string
}

type NavOrganization = {
  id: number
  name: string
  locations: NavLocation[]
}

export type NavContext = {
  currentLocation: NavLocation | null
  organizations: NavOrganization[]
  ungroupedLocations: NavLocation[]
  canCreateLocation: boolean
  canManageLocation: boolean
}

/**
 * The one context control in the staff sidebar. It names where you are — the
 * branch you are operating, or the organization you are administering — and
 * opens onto the hierarchy the data already has.
 *
 * **The invariant it must never break:** a location appears only when the
 * viewer holds a `Location::User` for it, and a group heading only when they
 * hold an `OrganizationMembership`. Belonging to a group does not open its
 * branches. The server enforces this by sending only what the viewer may see
 * (`SharedNavContext`); this component renders what it is given and adds
 * nothing.
 *
 * **Why a group row switches branch first.** The workspace resolves its group
 * from the location you are standing in — no route names an organization. So
 * "take me to this group" has to mean "stand in one of its branches, then open
 * the workspace", which is the `to: organization` switch below. A group you
 * belong to but are staff nowhere in cannot be opened at all; it renders as an
 * explanatory line rather than a dead row, because the reason is not obvious.
 */
export function StaffContextSwitcher({
  className,
  testId = "context-switcher",
  compact = false,
  onNavigate,
}: {
  className?: string
  testId?: string
  /** Collapsed rail: the label goes, the icon stays. */
  compact?: boolean
  /** Dismisses the mobile Sheet. A tap inside it that leaves it open strands
   *  the reader on the page they were already on, behind a panel. */
  onNavigate?: () => void
}) {
  // Both signals come from the server rather than from the URL.
  // `currentOrganization` is shared only by the workspace controllers, so its
  // presence is what marks "you are administering a group" — the component
  // never matches paths, which would break the moment a route moved.
  const { navContext, currentOrganization, navLabel } = usePage<{
    navContext?: NavContext | null
    currentOrganization?: { name: string } | null
    navLabel?: string | null
  }>().props

  if (!navContext) return null

  const { currentLocation, organizations, ungroupedLocations, canCreateLocation, canManageLocation } =
    navContext

  // What the control is currently pointing at: the organization while you are
  // administering one, the branch while you are operating it, the screen itself
  // on the creation forms, which are neither.
  const contextLabel =
    navLabel ??
    currentOrganization?.name ??
    currentLocation?.name ??
    t("ui.react.components.layouts.staff_context_switcher.current_context")

  // Nothing to administer and nowhere to operate: the navbar shows the brand
  // instead.
  if (organizations.length === 0 && ungroupedLocations.length === 0) return null

  // Every row navigates, including the one you are already on. A radio group
  // would be the obvious primitive, but it only fires when the value changes,
  // so re-picking your current location would do nothing. The control means one
  // thing everywhere: take me there.
  const navigate = (go: () => void) => {
    onNavigate?.()
    go()
  }

  const goToLocation = (location: NavLocation) =>
    navigate(() => {
      if (location.id === currentLocation?.id) {
        router.visit("/dashboard")
        return
      }

      router.post(`/locations/${location.id}/use_location`)
    })

  const goToOrganization = (organization: NavOrganization) =>
    navigate(() => {
      const standingHere = organization.locations.some((l) => l.id === currentLocation?.id)

      if (standingHere) {
        router.visit("/organization")
        return
      }

      router.post(`/locations/${organization.locations[0].id}/use_location`, { to: "organization" })
    })

  const renderLocations = (locations: NavLocation[]) => (
    <>
      {locations.map((location) => (
        <DropdownMenuItem
          key={location.id}
          data-testid={`context-location-option-${location.id}`}
          onSelect={() => goToLocation(location)}
        >
          {location.id === currentLocation?.id ? (
            <Check className="size-4" aria-hidden="true" />
          ) : (
            <span className="size-4" aria-hidden="true" />
          )}
          <span className="truncate">{location.name}</span>
        </DropdownMenuItem>
      ))}
    </>
  )

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          aria-label={`${t("ui.react.components.layouts.staff_context_switcher.switch_context")}: ${contextLabel}`}
          className={cn(
            "text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-ring flex w-full items-center rounded-md text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none",
            compact ? "size-11 justify-center md:size-9" : "h-11 gap-3 px-3 md:h-9",
            className
          )}
        >
          <Building2 className="size-4 shrink-0" aria-hidden="true" />
          {/* It names where you are: the organization while you are
              administering one, the branch while you are operating it. */}
          {!compact && (
            <>
              <span className="flex-1 truncate text-left">{contextLabel}</span>
              <ChevronsUpDown className="size-4 shrink-0 opacity-50" aria-hidden="true" />
            </>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="start"
        side="right"
        className="max-h-[70vh] w-[min(16rem,calc(100vw-2rem))] overflow-y-auto"
      >
        {/* No "where you are" label. The trigger already names the current
            context and the checked row marks it inside the list; a heading
            saying so a third time only pushed the first branch down. */}

        {/* Branches whose group is not the viewer's: no heading, because drawing
            one would name a group they are not a member of. */}
        {ungroupedLocations.length > 0 ? renderLocations(ungroupedLocations) : null}

        {organizations.map((organization, index) => (
          <div key={organization.id}>
            {ungroupedLocations.length > 0 || index > 0 ? <DropdownMenuSeparator /> : null}

            {organization.locations.length > 0 ? (
              <DropdownMenuItem
                data-testid={`context-organization-${organization.id}`}
                onSelect={() => goToOrganization(organization)}
                className="font-semibold"
              >
                <Building2 className="size-4" />
                <span className="truncate">{organization.name}</span>
              </DropdownMenuItem>
            ) : (
              // A group with no branch of yours has no workspace to open, since
              // the workspace resolves from the branch you are standing in. The
              // heading still renders — you are a member and hiding it would be
              // a lie — but it does not pretend to be a destination.
              <>
                <DropdownMenuLabel className="flex items-center gap-2 font-semibold">
                  <Building2 className="size-4" />
                  <span className="truncate">{organization.name}</span>
                </DropdownMenuLabel>
                <p
                  className="text-muted-foreground px-2 py-1.5 pl-8 text-xs italic"
                  data-testid={`context-organization-empty-${organization.id}`}
                >
                  {t("ui.react.components.layouts.staff_context_switcher.no_locations")}
                </p>
              </>
            )}

            {renderLocations(organization.locations)}
          </div>
        ))}

        {/* The directory of every branch, with its status and how far along its
            setup is -- more than a switcher row can carry. It is also where a
            stale current-branch cookie lands you, so leaving it unlinked would
            orphan a live page. */}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          data-testid="context-all-locations"
          onSelect={() => navigate(() => router.visit("/locations"))}
        >
          <Store className="size-4" />
          {t("ui.react.components.layouts.staff_context_switcher.locations")}
        </DropdownMenuItem>

        {/* The organization rows above lead to their workspace; this is the same
            move for the branch you are operating. */}
        {canManageLocation && currentLocation ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-testid="context-manage-location"
              onSelect={() => navigate(() => router.visit("/manage"))}
            >
              <SlidersHorizontal className="size-4" />
              {t("ui.react.components.layouts.staff_context_switcher.manage_location")}
            </DropdownMenuItem>
          </>
        ) : null}

        {canCreateLocation ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-testid="context-new-location"
              onSelect={() => navigate(() => router.visit("/locations/new"))}
            >
              <Plus className="size-4" />
              {t("ui.navigation.new_location")}
            </DropdownMenuItem>
            {/* Opening a group is opening a second billing account, so it sits
                below the branch entry rather than beside it: the common move is
                another branch in the group you already pay for. The screen it
                leads to says what the second bill means. */}
            <DropdownMenuItem
              data-testid="context-new-organization"
              onSelect={() => navigate(() => router.visit("/organizations/new"))}
            >
              <Building2 className="size-4" />
              {t("ui.navigation.new_organization")}
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
